// UC-3/UC-4. Two modes:
// - Real backend (SessionApiBundle present): calls POST /session/:id/complete and streams the
//   real UC-2c reflected problem, reasoning and result (design/2026-08-26-session-api-contract.md).
// - Mock preview (no bundle): runs the real deterministic filter (UC-2a/UC-2b, src/matching/)
//   against the mock roster with a short simulated wait and canned copy from mock-data.ts, since
//   that's what the preview harness runs without a backend.

import type { ConfigVersion, Solution } from "../types";
import type { DiscoverySessionState } from "../session/state";
import { toAnswersJson } from "../session/state";
import { deriveProblemProfile } from "../matching/problem-profile";
import { selectCandidateSet } from "../matching/candidate-set";
import type { AnswerInput } from "../matching/types";
import { mockAlternateReasonFor, mockRecommendationFor } from "./mock-data";
import * as apiClient from "./api-client";
import type { SessionApiBundle } from "./question-flow";
import { buildBookingUrl } from "../booking-url";
import type { SessionCampaign } from "../campaign";
import { displayAttributes } from "../attribute-label";
import { ALTERNATES_SUBHEAD } from "../matching/alternate-copy";
import { loadingHtml, startLoadingSteps, type LoadingSteps } from "./loading";

/** What the reveal card needs for one Board Member, top match or otherwise. */
interface ResultCard {
  /** Alternates only: which Board Member this card is, so a late-arriving reason finds its card. */
  id?: string;
  name: string;
  oneLiner: string;
  bookingUrl: string;
  attributes?: Solution["attributes"];
  /** Alternates only: the matcher's two-sentence "why this one, and why not first." Absent when the
   * model skipped it; the card then renders exactly as it did before reasons existed. */
  reason?: string;
  /** Alternates only: the reason is still on its way from the alternates call (2026-09-25 plan).
   * Renders a placeholder that `fillAlternateReasons` later replaces or removes. */
  reasonPending?: boolean;
}

/** Model-generated text goes into innerHTML below, unlike the config-authored fields around it,
 * so it's escaped rather than trusted. */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export interface ResultScreenHost {
  /** The visitor wants a fresh session ("Got another problem?"). The host decides whether to ask
   * first: embed.ts treats this like closing the panel, so it confirms while the match is still
   * unactioned. */
  onRestart: () => void;
  /** Fired once the result screen has something actionable on it (a matched card or the fallback
   * message) — the signal embed.ts uses to start guarding close() behind a confirmation. */
  onResultShown?: () => void;
  /** Fired when the visitor takes one of the result screen's own CTAs (Book Now, Grab Report, or
   * Find a Board Member on the fallback screen) — clears the close-confirmation guard, since
   * there's nothing left to accidentally lose. */
  onResultActionTaken?: () => void;
}

/** Mock preview only: long enough for the loading steps to be seen advancing. */
const MATCHING_DELAY_MS = 3600;
const LOADING_STEP_INTERVAL_MS = 1800;

/** Placeholder copy from Sarah's style suggestions, pending the same voice review as the rest. */
const LOADING_STEPS = [
  "Reading through what you told us",
  "Getting a clear picture of what's going on",
  "Finding your best-fit Board Member",
];

const BOOK_LABEL = "Learn More &amp; Book a Session";
const REPORT_LABEL = "Save PDF Recommendation";
const RESTART_LABEL = "Got another problem? Click here to restart the experience.";

export class ResultScreen {
  private loadingSteps?: LoadingSteps;

  /** Name and email from the session's own pii-type answer, if it has one — threaded into
   * buildBookingUrl (src/booking-url.ts) so both this screen's and the report's booking links stay
   * on the same seam. Read directly off the local session state rather than a new API response
   * field, since this data is already there (same lookup routes.ts:289 does server-side). */
  private readonly visitor?: { firstName?: string; lastName?: string; email?: string };

  constructor(
    private readonly container: HTMLElement,
    private readonly config: ConfigVersion,
    private readonly session: DiscoverySessionState,
    private readonly host: ResultScreenHost,
    private readonly apiBundle?: SessionApiBundle,
    /** docs/plans/2026-10-13-campaigns.md: shown as an offer callout, and its code rides along on
     * every booking link. */
    private readonly campaign?: SessionCampaign
  ) {
    const questionsById = new Map(config.questions.map((q) => [q.id, q]));
    this.visitor = session.answers
      .filter((a) => !a.supersededAt)
      .find((a) => questionsById.get(a.questionId)?.answerType === "pii")?.piiValue;
  }

  async mount(): Promise<void> {
    this.renderWaiting();
    if (this.apiBundle) {
      await this.mountLive(this.apiBundle);
    } else {
      await new Promise((r) => setTimeout(r, MATCHING_DELAY_MS));
      this.renderResult();
    }
  }

  private async mountLive(bundle: SessionApiBundle): Promise<void> {
    try {
      let reflectedProblem = "";
      let reasoning = "";
      await apiClient.complete(bundle.baseUrl, bundle.sessionId, {
        onReflectedProblem: (text) => {
          reflectedProblem = text;
          // A real signal: the model has started answering, so the last step ("finding your
          // best-fit") is now the honest one to show.
          this.loadingSteps?.finish();
        },
        onReasoning: (text) => (reasoning = text),
        onResult: (result) => {
          if (result.outcome !== "matched" || !result.solution || !result.chosenSolutionId) {
            this.renderFallback();
            return;
          }
          // The stream's `solution` field only carries the top match's presentable fields
          // (design/2026-08-26-session-api-contract.md's ResultPayload) — the rest of
          // rankedSolutionIds are just IDs, so the other cards' name/oneLiner/attributes are
          // resolved off the config already loaded client-side, same as report-template.ts does
          // server-side against its own solutionsById map.
          const solutionsById = new Map(this.config.solutions.map((s) => [s.id, s]));
          // A fresh /complete carries no reasons; they arrive from a second call once this screen
          // is up (2026-09-25 plan). A replayed /complete whose second call already finished
          // carries them, and then there's nothing to fetch.
          const replayedReasons = result.alternateReasons ?? [];
          const fetchReasons = replayedReasons.length === 0;
          const reasonsById = new Map(replayedReasons.map((r) => [r.solutionId, r.reason]));
          const others = result.rankedSolutionIds
            .filter((id) => id !== result.chosenSolutionId)
            .slice(0, this.config.settings.maxAlternates)
            .map((id) => solutionsById.get(id))
            .filter((s): s is Solution => !!s)
            .map((s) => ({ ...this.toResultCard(s, reflectedProblem), id: s.id, reason: reasonsById.get(s.id), reasonPending: fetchReasons }));

          this.reveal(
            {
              name: result.solution.name,
              oneLiner: result.solution.oneLiner,
              bookingUrl: this.bookingUrlFor(result.solution, reflectedProblem),
              attributes: result.solution.attributes,
            },
            reflectedProblem,
            reasoning,
            others
          );
          if (fetchReasons && others.length > 0) void this.loadAlternateReasons(bundle);
        },
      });
    } catch {
      // Fail-closed per CLAUDE.md: a transport-level failure gets the same "we don't have a
      // confident match" screen a real ADR-008 fallback outcome would, rather than a stuck spinner.
      this.renderFallback();
    }
  }

  /** Fills in the alternates' reasons once the second call answers, or quietly drops the
   * placeholders if it doesn't. Never touches the top match, and never shows an error: a missing
   * reason is an ordinary, already-supported state for an alternate card. */
  private async loadAlternateReasons(bundle: SessionApiBundle): Promise<void> {
    let reasons: { solutionId: string; reason: string }[] = [];
    try {
      reasons = await apiClient.fetchAlternates(bundle.baseUrl, bundle.sessionId);
    } catch {
      // Fall through with no reasons; the placeholders are removed below.
    }
    const byId = new Map(reasons.map((r) => [r.solutionId, r.reason]));
    for (const el of this.container.querySelectorAll<HTMLElement>(".bfc-result-alt-reason-pending")) {
      const reason = el.dataset.altId ? byId.get(el.dataset.altId) : undefined;
      if (reason) {
        el.textContent = reason;
        el.classList.remove("bfc-result-alt-reason-pending");
        el.removeAttribute("aria-busy");
      } else {
        el.remove();
      }
    }
  }

  private renderWaiting(): void {
    this.loadingSteps?.stop();
    this.container.innerHTML = `<div class="bfc-fade-enter">${loadingHtml({
      label: "Finding your match",
      heading: "Hang tight, I'm working on it.",
      steps: LOADING_STEPS,
    })}</div>`;
    this.loadingSteps = startLoadingSteps(this.container, LOADING_STEP_INTERVAL_MS);
  }

  private renderResult(): void {
    const answers: AnswerInput[] = Object.entries(toAnswersJson(this.session, this.config.questions)).map(
      ([questionId, a]) => ({ questionId, selectedOptionIds: a.selectedOptionIds })
    );
    const profile = deriveProblemProfile(answers, this.config.questions, this.config.settings.tagInclusionFloor);
    const candidates = selectCandidateSet(profile, this.config.solutions);

    if (candidates.isEmpty) {
      this.renderFallback();
      return;
    }

    const topId = candidates.solutionIds[0]!;
    const solution = this.config.solutions.find((s) => s.id === topId)!;
    const rec = mockRecommendationFor(solution);

    const others = candidates.solutionIds
      .slice(1, 1 + this.config.settings.maxAlternates)
      .map((id) => this.config.solutions.find((s) => s.id === id))
      .filter((s): s is Solution => !!s)
      .map((s) => ({ ...this.toResultCard(s, rec.reflectedProblem), reason: mockAlternateReasonFor(s, solution) }));

    this.reveal(this.toResultCard(solution, rec.reflectedProblem), rec.reflectedProblem, rec.reasoning, others);
  }

  private bookingUrlFor(solution: Pick<Solution, "bookingUrl">, problemStatement: string): string {
    return buildBookingUrl(solution, this.config.settings.findABoardMemberUrl, {
      visitorFirstName: this.visitor?.firstName,
      visitorLastName: this.visitor?.lastName,
      visitorEmail: this.visitor?.email,
      problemStatement,
      campaignCode: this.campaign?.code,
    });
  }

  /** Sheet-authored, but escaped anyway: it's free text, and the only Campaigns field rendered. */
  private campaignHtml(): string {
    if (!this.campaign) return "";
    return `
      <div class="bfc-result-campaign">
        <p class="bfc-eyebrow bfc-eyebrow--small">Special offer</p>
        <p class="bfc-result-campaign-text">${escapeHtml(this.campaign.description)}</p>
      </div>
    `;
  }

  private toResultCard(solution: Solution, problemStatement: string): ResultCard {
    return {
      name: solution.name,
      oneLiner: solution.oneLiner,
      bookingUrl: this.bookingUrlFor(solution, problemStatement),
      attributes: solution.attributes,
    };
  }

  /** "Label: value" lines from the allowlisted subset of a Board Member's additional attributes
   * (see src/attribute-label.ts), shared markup between the top card and the other-options
   * cards below it. Same conversion and layout as the downloadable report's memberCard
   * (src/session/report-template.ts), so the two stay visually consistent. */
  private attributesHtml(attributes?: Solution["attributes"]): string {
    const entries = displayAttributes(attributes);
    if (entries.length === 0) return "";
    const lines = entries
      .map(([label, value]) => `<span class="bfc-result-attr"><strong>${label}:</strong> ${value}</span>`)
      .join("");
    return `<div class="bfc-result-attrs">${lines}</div>`;
  }

  private otherCardHtml(solution: ResultCard): string {
    return `
      <div class="bfc-result-card bfc-result-card-secondary">
        <p class="bfc-result-name">${solution.name}</p>
        <p class="bfc-result-oneliner">${solution.oneLiner}</p>
        ${
          solution.reason
            ? `<p class="bfc-result-alt-reason">${escapeHtml(solution.reason)}</p>`
            : solution.reasonPending && solution.id
              ? `<p class="bfc-result-alt-reason bfc-result-alt-reason-pending" data-alt-id="${escapeHtml(solution.id)}" aria-busy="true" aria-live="polite"></p>`
              : ""
        }
        ${this.attributesHtml(solution.attributes)}
        <a class="bfc-btn bfc-book" href="${solution.bookingUrl}" target="_blank" rel="noopener">${BOOK_LABEL}</a>
      </div>
    `;
  }

  /** Shared progressive-reveal card, driving both the mock canned copy and the real streamed
   * reflectedProblem/reasoning through the same `typeInto` animation (reflectedProblem first, per
   * the decomposition doc's UC-3 recommendation and the contract's SSE event ordering). `others` is
   * the rest of the ranked candidates (UC-3/UC-4), shown below the top match in a quieter
   * secondary treatment. */
  private reveal(solution: ResultCard, reflectedProblem: string, reasoning: string, others: ResultCard[] = []): void {
    const el = document.createElement("div");
    // "Grab Report" only exists against a real session (GET /session/:id/report needs one to
    // read) — mock-preview mode (no apiBundle) has nothing for that route to serve.
    const reportLink = this.apiBundle
      ? `<a class="bfc-report" href="${this.apiBundle.baseUrl}/session/${this.apiBundle.sessionId}/report" target="_blank" rel="noopener">${REPORT_LABEL}</a>`
      : "";
    const othersHtml = others.length
      ? `
        <div class="bfc-result-others">
          <button type="button" class="bfc-result-others-toggle" aria-expanded="false" aria-controls="bfc-result-others-body">
            <span class="bfc-result-others-label">Other Board Members worth a look <span class="bfc-result-others-count">(${others.length})</span><span class="bfc-result-others-cta">(Click to expand)</span></span>
            <span class="bfc-result-others-chevron" aria-hidden="true"></span>
          </button>
          <div class="bfc-result-others-body" id="bfc-result-others-body" hidden>
            <div class="bfc-result-others-body-inner">
              <p class="bfc-result-others-subhead">${ALTERNATES_SUBHEAD}</p>
              ${others.map((s) => this.otherCardHtml(s)).join("")}
            </div>
          </div>
        </div>
      `
      : "";
    // Two numbered points, not three: the matcher produces exactly a problem statement
    // (reflectedProblem) and a recommendation with its reasoning, and splitting those further would
    // be arbitrary. No Board Member photo yet either; the config carries none.
    el.innerHTML = `
      <p class="bfc-eyebrow">Your best-fit Board Member</p>
      <ol class="bfc-timeline">
        <li class="bfc-timeline-item">
          <span class="bfc-timeline-badge" aria-hidden="true">1</span>
          <p class="bfc-eyebrow bfc-eyebrow--small">Here's what we think is really going on</p>
          <p class="bfc-timeline-text"></p>
        </li>
        <li class="bfc-timeline-item">
          <span class="bfc-timeline-badge" aria-hidden="true">2</span>
          <p class="bfc-eyebrow bfc-eyebrow--small">Here's who we'd recommend for that, and why</p>
          <p class="bfc-timeline-text bfc-timeline-text--soft"></p>
        </li>
      </ol>
      <div class="bfc-result-card bfc-fade-enter" style="opacity:0">
        <p class="bfc-result-name">${solution.name}</p>
        <p class="bfc-result-oneliner">${solution.oneLiner}</p>
        ${this.attributesHtml(solution.attributes)}
        ${this.campaignHtml()}
        <a class="bfc-btn bfc-book" href="${solution.bookingUrl}" target="_blank" rel="noopener">${BOOK_LABEL}</a>
        ${reportLink}
      </div>
      ${othersHtml}
      ${this.restartHtml()}
    `;
    this.loadingSteps?.stop();
    this.container.innerHTML = "";
    this.container.appendChild(el);

    const [heading, reasoningEl] = Array.from(el.querySelectorAll<HTMLElement>(".bfc-timeline-text")) as [HTMLElement, HTMLElement];
    const card = el.querySelector<HTMLElement>(".bfc-result-card")!;

    for (const cta of el.querySelectorAll<HTMLAnchorElement>(".bfc-book, .bfc-report")) {
      cta.addEventListener("click", () => this.host.onResultActionTaken?.());
    }
    this.setUpOthersToggle(el);
    this.wireRestart(el);
    this.host.onResultShown?.();

    void this.typeInto(heading, reflectedProblem).then(async () => {
      await this.typeInto(reasoningEl, reasoning);
      card.style.opacity = "1";
      card.animate([{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "translateY(0)" }], {
        duration: this.durationMs(),
        easing: this.easing(),
      });
    });
  }

  private renderFallback(): void {
    this.loadingSteps?.stop();
    this.container.innerHTML = `
      <div class="bfc-waiting bfc-fade-enter">
        <p class="bfc-result-heading">We want to get this right.</p>
        <p>Based on what you told us, we don't have a confident match yet. Have a look at the full board instead.</p>
        ${this.campaignHtml()}
        <a class="bfc-btn bfc-book" href="${this.config.settings.findABoardMemberUrl}" target="_blank" rel="noopener">Find a Board Member</a>
        ${this.restartHtml()}
      </div>
    `;
    this.container.querySelector(".bfc-book")?.addEventListener("click", () => this.host.onResultActionTaken?.());
    this.wireRestart(this.container);
    this.host.onResultShown?.();
  }

  private restartHtml(): string {
    return `<button type="button" class="bfc-restart">${RESTART_LABEL}</button>`;
  }

  private wireRestart(root: HTMLElement): void {
    root.querySelector(".bfc-restart")?.addEventListener("click", () => this.host.onRestart());
  }

  /** Wires the "Other Board Members" header as a collapsed-by-default disclosure toggle. Height is
   * animated via max-height (measured off scrollHeight) rather than a CSS-only grid trick, so it
   * degrades cleanly under prefers-reduced-motion the same way typeInto does. */
  private setUpOthersToggle(root: HTMLElement): void {
    const toggle = root.querySelector<HTMLButtonElement>(".bfc-result-others-toggle");
    const body = root.querySelector<HTMLElement>(".bfc-result-others-body");
    if (!toggle || !body) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    body.style.maxHeight = "0px";

    toggle.addEventListener("click", () => {
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      const willOpen = !isOpen;
      toggle.setAttribute("aria-expanded", String(willOpen));
      toggle.classList.toggle("bfc-result-others-toggle-open", willOpen);

      if (reduced) {
        body.hidden = !willOpen;
        body.style.maxHeight = willOpen ? "none" : "0px";
        return;
      }

      if (willOpen) {
        body.hidden = false;
        // Force layout so the browser has a starting height (0px) to transition from.
        const targetHeight = body.scrollHeight;
        body.style.maxHeight = "0px";
        void body.offsetHeight;
        body.style.maxHeight = `${targetHeight}px`;
      } else {
        body.style.maxHeight = `${body.scrollHeight}px`;
        void body.offsetHeight;
        body.style.maxHeight = "0px";
      }
    });

    body.addEventListener("transitionend", (e) => {
      if (e.propertyName !== "max-height") return;
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      if (!isOpen) {
        body.hidden = true;
      } else {
        // Let content that grows later (e.g. font load) keep fitting without a re-toggle.
        body.style.maxHeight = "none";
      }
    });
  }

  private async typeInto(el: HTMLElement, text: string): Promise<void> {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      el.textContent = text;
      return;
    }
    const words = text.split(" ");
    el.textContent = "";
    for (const word of words) {
      el.textContent += (el.textContent ? " " : "") + word;
      await new Promise((r) => setTimeout(r, 28));
    }
  }

  private durationMs(): number {
    const raw = getComputedStyle(this.container).getPropertyValue("--bfc-motion-duration-base").trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 220;
  }

  private easing(): string {
    return getComputedStyle(this.container).getPropertyValue("--bfc-motion-ease").trim() || "ease-out";
  }
}

export function renderOptOutScreen(container: HTMLElement, config: ConfigVersion): void {
  container.innerHTML = `
    <div class="bfc-optout-screen bfc-fade-enter">
      <p class="bfc-result-heading">No problem.</p>
      <p class="bfc-reasoning">You can browse the full board of experts instead.</p>
      <a class="bfc-btn bfc-book" href="${config.settings.findABoardMemberUrl}" target="_blank" rel="noopener">Find a Board Member</a>
    </div>
  `;
}
